// Installable app plumbing: service worker lifecycle, install prompt, standalone detection.
import { signal } from '@preact/signals';

/** Chrome/Android/desktop: the deferred install prompt, when the browser offers one. */
export const installPrompt = signal<(() => Promise<boolean>) | null>(null);
/** iOS Safari has no prompt: the app is added from the share sheet. */
export const iosHint = signal(false);
/** A new build is waiting: reload to get it. */
export const updateReady = signal<(() => void) | null>(null);

export const isStandalone = (): boolean => window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

export function setupPwa(): void {
  if (isStandalone()) { try { localStorage.setItem('aurane.installed', '1'); } catch { /* ignore */ } }
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (ios && !isStandalone()) iosHint.value = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    const ev = e as Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
    installPrompt.value = async () => { await ev.prompt(); const { outcome } = await ev.userChoice; if (outcome === 'accepted') installPrompt.value = null; return outcome === 'accepted'; };
  });
  window.addEventListener('appinstalled', () => { installPrompt.value = null; iosHint.value = false; });

  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;
  // A first visit has no controller: when the new worker claims the page, nothing changes for the player, so no reload.
  // Reloading there wiped the founding form 1-2 s after the page opened (name typed, faction and General clicked: QA
  // 10.10, the « lost clicks » of the outside tester and of brosim). Reload only to swap a running build for a new one.
  const hadController = !!navigator.serviceWorker.controller;
  // A page loaded from the network after a deploy already runs the new build while its worker still waits (QA 11.10:
  // « new version ready » on the current bundle). Ask the waiting worker what it precaches: when this page's own bundle
  // is in it, activate it silently (no banner, no reload); otherwise, or without an answer, offer the reload.
  let silent = false;
  const mine = new URL(import.meta.url).pathname;
  const offer = (sw: ServiceWorker): void => {
    const ch = new MessageChannel();
    const timer = setTimeout(() => { updateReady.value = () => sw.postMessage('skip-waiting'); }, 2000);
    ch.port1.onmessage = (e: MessageEvent) => {
      clearTimeout(timer);
      if (Array.isArray(e.data) && e.data.includes(mine)) { silent = true; sw.postMessage('skip-waiting'); }
      else updateReady.value = () => sw.postMessage('skip-waiting');
    };
    sw.postMessage('precache', [ch.port2]);
  };
  navigator.serviceWorker.register('/sw.js').then((reg) => {
    const track = (sw: ServiceWorker | null): void => {
      if (!sw) return;
      sw.addEventListener('statechange', () => {
        if (sw.state === 'installed' && navigator.serviceWorker.controller) offer(sw);
      });
    };
    if (reg.waiting && navigator.serviceWorker.controller) offer(reg.waiting);
    track(reg.installing);
    reg.addEventListener('updatefound', () => track(reg.installing));
    // Look for a new build when the app comes back to the foreground.
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void reg.update(); });
  }).catch(() => undefined);
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => { if (reloading || !hadController || silent) return; reloading = true; location.reload(); });
}

// Web Push (issue #48): this device's alerts. Needs the service worker (production build), the Push API, and on
// iPhone the app added to the home screen (iOS only offers push to installed web apps).
export type PushState = 'unsupported' | 'ios-install' | 'off' | 'on' | 'denied';

export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return (/iPhone|iPad|iPod/.test(navigator.userAgent) && !isStandalone()) ? 'ios-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return sub ? 'on' : 'off';
}

const b64ToBytes = (b64: string): Uint8Array<ArrayBuffer> => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

/** Asks the browser, subscribes with the server's key, hands the subscription to the server. */
export async function enablePush(publicKey: string, headers: Record<string, string>): Promise<boolean> {
  if ((await Notification.requestPermission()) !== 'granted') return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(publicKey) });
  const res = await fetch('/api/push/subscribe', { method: 'POST', headers: { ...headers, 'content-type': 'application/json' }, body: JSON.stringify(sub.toJSON()) });
  if (!res.ok) { await sub.unsubscribe(); return false; }
  return true;
}

export async function disablePush(headers: Record<string, string>): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (!sub) return;
  await fetch('/api/push/unsubscribe', { method: 'POST', headers: { ...headers, 'content-type': 'application/json' }, body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => undefined);
  await sub.unsubscribe();
}
